import { action, observable, computed, when } from 'mobx';

import { AppManager, Input, Variables } from 'core/index';
import { routerStore } from 'core/routing';
import { FormStore } from 'libs/forms/app';
import { RFQListByIDSQuery } from 'apps/rfq/gql';
import { RFQArrayApp, RFQModel } from 'apps/rfq/app';
import { parseISO, formatISO } from 'date-fns';

import { whenever } from 'core/whenever';

import { productQuoteFromRFQ } from 'apps/productQuote/utils';

import {
  ProductQuoteModel,
  ProductQuotesArrayApp,
} from 'apps/productQuote/app';

import { ProductQuotesById } from 'apps/productQuote/gql';

import { graphqlIdToDecimal, graphqlIdToDBId } from 'utils/dgraphId';

import Alert from 'react-s-alert';

import { clientStore } from 'apps/client/app';

import { modalStore } from 'apps/modals/app';

import { shipmentQuoteConfig } from './shipmentQuoteConfig';
import { miscFormConfig } from './miscFormConfig';
import { shippingQuoteConfig } from './ShippingDetails/ShippingDetailsForm/shippingFormConfig';
import { AddId, RemoveId, ShipmentQuoteNavigate } from './utils';
import {
  MiscItemApp,
  ShipmentQuoteApp,
  ShipmentQuoteModel,
  ShippingQuoteApp,
} from './app';
import { CreateShipmentQuoteMutation } from './gql';
import { EditPartsModal } from './EditPartsModal';

const convertToGraphqlIds = (ids, type = 'RFQ') => {
  return ids.map(id => window.btoa(`${type}:0x${id}`));
};

const onlyUniqueById = (value, index, self) => {
  return self.findIndex(v => v.id === value.id) === index;
};

export default class CreateShipmentQuoteAppManager extends AppManager {
  @observable productQuote = null;
  @observable productQuotes = [];

  @observable rfqs = [];
  @observable currentProductQuoteAppManager;
  @observable miscItemFormStore;

  customerSpaceId = null;
  canInitializeValuesFromPQs = false;
  prevLocalProductQuoteApps = [];
  /* A lot of this AppManagers attributed and computed methods could and probably should be 
  moved to ShipmentQuoteApp */

  constructor(parent, props, options) {
    super(parent, props, options);
    this.AppRef('rfqs', RFQArrayApp);
    this.AppRef('productQuotes', ProductQuotesArrayApp);
    this.initialiseRFQs();
    this.initialiseQuoteForm();
    this.initialiseProductQuotes();

    if (this.pqids.length > 0) {
      this.canInitializeValuesFromPQs = true;
    }

    this.disposers.push(
      whenever(
        () => this.shipmentQuoteApp.submitting,
        () => {
          this.shipmentQuoteMutation.mutate();
        },
      ),
      whenever(
        () =>
          routerStore.route.name === 'new-quote' &&
          // above clause is required because the AppManager is disposed after the route has already changed
          this.rfqids.length === 0 &&
          this.pqids.length === 0,
        () => {
          routerStore.navigate(
            'workspace.intermediator.rfqs',
            {
              spacePath: clientStore.activeSpace?.path,
            },
            { replace: true },
          );
        },
      ),
      when(
        () => this.rfqs.length > 0 || this.productQuotes.length > 0,
        () => {
          this.customerSpaceId =
            this.rfqs?.apps?.[0]?.customerSpace.id ||
            this.productQuotes?.apps?.[0]?.from.customerSpace.id;
        },
      ),
    );
  }

  @computed get rfqids() {
    return routerStore.params.get('rfqids') || [];
  }

  @computed get pqids() {
    return routerStore.params.get('pqids') || [];
  }

  @action AddOrRemoveProductQuoteId(productQuote) {
    if (this.pqids.includes(productQuote.dgraphId)) {
      this.RemoveProductQuoteId(productQuote);
    } else {
      this.AddProductQuoteId(productQuote);
    }
  }

  @action AddProductQuoteId = productQuote => {
    const newParams = {
      pqids: AddId(routerStore, 'pqids', productQuote),
    };

    // if rfqid is in the url remove it (we now have a real ProductQuote)
    if (this.rfqids.includes(productQuote.from.dgraphId)) {
      newParams.rfqids = RemoveId(routerStore, 'rfqids', productQuote.from);
    }
    ShipmentQuoteNavigate(routerStore, newParams);
  };

  @computed get numSelectedPqs() {
    // the number of productQuotes selected for this rfq
    if (this.currentProductQuoteAppManager?.productQuotes) {
      return this.currentProductQuoteAppManager.productQuotes.filter(pq =>
        this.pqids.includes(pq.dgraphId),
      ).length;
    }
    return 0;
  }

  @action RemoveProductQuoteId = productQuote => {
    const newParams = {
      pqids: RemoveId(routerStore, 'pqids', productQuote),
    };
    // if only one pqid for this rfq and we are removing it re-add the rfqid
    if (this.numSelectedPqs <= 1) {
      newParams.rfqids = AddId(routerStore, 'rfqids', productQuote.from);
    }
    ShipmentQuoteNavigate(routerStore, newParams);
  };

  initialiseProductQuotes = () => {
    this.pqVariables = new Variables({
      ids: () => convertToGraphqlIds(this.pqids, 'ProductQuote'),
    });
    this.productQuotesQ = new ProductQuotesById(this, {
      queryKey: 'pqsByIds',
      variables: this.pqVariables,
    });
    this.productQuotesQ.process(
      pqs => {
        if (pqs === null) {
          return [];
        }
        return pqs.map(pq => ProductQuoteModel.Load(pq));
      },
      models => {
        this.UpdateProductQuotes(models);
      },
    );
    this.productQuotesQ.query();

    this.pqVariables.onChange(['ids'], this.refetchProductQuotes, {
      fireImmediately: false,
    });
  };

  refetchProductQuotes = () => {
    this.productQuotesQ.refetchQuery();
  };

  @action UpdateProductQuotes = pqs => {
    this.productQuotes = pqs;
    if (this.canInitializeValuesFromPQs && this.productQuotes?.length > 0) {
      this.shipmentQuoteApp.currency = this.productQuotes.apps[0].currency;
      this.shipmentQuoteApp.manufacturer = this.productQuotes.apps[0].supplier;
      this.canInitializeValuesFromPQs = false;
    }
  };

  initialiseRFQs = () => {
    this.variables = new Variables({
      ids: () => convertToGraphqlIds(this.rfqids),
    });
    this.rfqsQuery = new RFQListByIDSQuery(this, {
      queryKey: 'rfqsByIDs',
      // variables: this.variables,
    });
    this.rfqsQuery.process(rfqs => {
      if (rfqs === null) {
        return [];
      }
      return rfqs.map(rfq => RFQModel.Load(rfq));
    }, this.UpdateRFQList);
    this.variables.onChange(['ids'], this.refetchRFQs, {
      fireImmediately: false,
    });
    this.rfqsQuery.query();
  };

  @computed get pqsReady() {
    return !this.rfqsQuery.loading && !this.productQuotesQ.loading;
  }

  @computed get pqsWithoutAddress() {
    return this.productQuoteApps.filter(pq => !pq.from.address);
  }

  @computed get addresses() {
    /* Get the address from the rfqs or productQuotes */
    if (this.pqsReady) {
      return this.productQuoteApps
        .map(pqApp => pqApp?.from?.address)
        .filter(addr => !!addr)
        .filter(onlyUniqueById);
    }
    // should probably have an error value/symbol in these kinds of situations
    // i.e. we should distinguish between not ready and no addresses
    return [];
  }

  @computed get someEmptyAddresses() {
    if (this.pqsReady) {
      return this.productQuoteApps.some(pqApp => !pqApp.from.address);
    }
    // should probably have an error value/symbol if ProductQuotes are loaded
    // for now we only care if there are some empty addresses i.e. true case
    return null;
  }

  @computed get customerMismatch() {
    if (this.pqsReady) {
      return this.productQuotes.some(
        pq =>
          pq.from.customerSpace.id !==
          this.productQuotes.apps[0].from.customerSpace.id,
      );
    }
    return null;
  }

  @computed get currencyMismatch() {
    if (this.pqsReady) {
      const selectedCurrency = this.shipmentQuoteApp.currency;
      return this.productQuotes.some(pq => pq.currency !== selectedCurrency);
    }
    return null;
  }

  @computed get supplierMismatch() {
    if (this.pqsReady) {
      const selectedSupplierId = this.shipmentQuoteApp.manufacturer?.id;
      if (!selectedSupplierId) {
        return this.productQuoteApps.length - this.numUnquotedRfqs > 0;
      }

      return this.productQuotes.some(
        pq => pq.supplier.id !== selectedSupplierId,
      );
    }
    return false;
  }

  @computed get validUntilMismatch() {
    const now = new Date();
    const { validUntil } = this.shipmentQuoteApp;
    if (validUntil && validUntil < now) return true;

    return this.productQuotes.some(pq => parseISO(pq.validUntil) < now);
  }

  @computed get unquotableRFQs() {
    return this.rfqs.filter(rfq => !rfq.isQuotable);
  }

  refetchRFQs = () => {
    this.rfqsQuery.refetchQuery();
  };

  @action SelectProductQuote = productQuote => {
    this.productQuote = productQuote;
  };

  @action UpdateRFQList = rfqs => {
    this.rfqs = rfqs;
  };

  productQuoteFromRFQ = rfqApp => {
    return productQuoteFromRFQ(this, rfqApp, this);
  };

  @computed get localProductQuoteApps() {
    let localPQApps = [];
    if (this.rfqs) {
      localPQApps = this.rfqs.map(rfq => this.productQuoteFromRFQ(rfq));

      // Note (david): This is an ad-hoc solution to keep values of edited fields
      // when something other than the rfqs changes.
      // Interesting to think what would be a better general approach.
      this.prevLocalProductQuoteApps.forEach(prevPQ => {
        const matchingPQs = localPQApps.filter(
          localPQ => localPQ.from.id === prevPQ.from.id,
        );
        const isItAnUnambiguousMatch = matchingPQs.length === 1;
        if (isItAnUnambiguousMatch) {
          matchingPQs[0].for.quantity = prevPQ.for.quantity;
          matchingPQs[0].unitCost = prevPQ.unitCost;
        }
      });
    }

    this.prevLocalProductQuoteApps = localPQApps;
    return localPQApps;
  }

  @computed get productQuoteApps() {
    /* Including ProductQuotes which already exist and have been selected.
    Sort the ProductQuotes consistently e.g. by filename or RFQ ID
     */
    return this.localProductQuoteApps
      .concat(this.productQuotes.slice())
      .sort((a, b) => {
        return (a.from.decimalId < b.from.decimalId && -1) || 1;
      });
  }

  @computed get numUnquotedRfqs() {
    return this.productQuoteApps.filter(pq => {
      const { cost } = pq;
      return typeof cost !== 'number' || cost < 0 || Number.isNaN(cost);
    }).length;
  }

  @action AddMiscItem = () => {
    const { shipmentQuoteApp } = this;
    shipmentQuoteApp.miscItems.push(this.miscItemFormStore.target);
    this.InitialiseMiscItemForm();
  };

  @action RemoveMiscItem = miscItemApp => {
    const { shipmentQuoteApp } = this;
    shipmentQuoteApp.miscItems.remove(miscItemApp);
  };

  @action InitialiseMiscItemForm = () => {
    this.shipmentQuoteApp.newMiscItem = new MiscItemApp(this.shipmentQuoteApp);
    this.miscItemFormStore = new FormStore(
      this.shipmentQuoteApp.newMiscItem,
      miscFormConfig,
      'MiscItemForm',
      false, // autoFocusFirstField
    );
  };

  @action initialiseQuoteForm = () => {
    // this.ResetQuoteFormData();
    this.shipmentQuoteApp = new ShipmentQuoteApp(this);
    this.shipmentQuoteApp.miscItems = [];
    this.shipmentQuoteFormStore = new FormStore(
      this.shipmentQuoteApp,
      shipmentQuoteConfig,
      'ShipmentQuoteForm',
      false, // autoFocusFirstField
    );

    // default form values
    this.shipmentQuoteApp.takeRate = 10;

    this.shipmentQuoteApp.shippingQuote = new ShippingQuoteApp(
      this.shipmentQuoteApp,
    );

    this.shippingQuoteFormStore = new FormStore(
      this.shipmentQuoteApp.shippingQuote,
      shippingQuoteConfig,
      'ShippingQuoteForm',
      false, // autoFocusFirstField
    );
    this.InitialiseMiscItemForm();
    this.shipmentQuoteInput = new Input({
      input: () => {
        const { validUntil, shippingQuote } = this.shipmentQuoteApp;

        let shippingQuoteInput = null;
        if (!this.areThereInstantQuotes) {
          shippingQuoteInput = {
            provider: shippingQuote.provider,
            deliveryTime: shippingQuote.deliveryTime,
            cost: shippingQuote.cost,
            discount: shippingQuote.discount,
            // currency: shippingQuote.currency,
            isTaxInclusive: shippingQuote.isTaxInclusive,
          };
        }

        return {
          shippingQuote: shippingQuoteInput,
          currency: this.shipmentQuoteApp.currency,
          addressId: this.addresses[0]?.id,
          productQuoteIds: this.productQuotes.map(pq => pq.id),
          productQuotes: this.localProductQuoteApps.map(pq => {
            return {
              fromId: pq.from.id,
              cost: pq.cost,
              currency: pq.currency,
              newJobSpec: {
                quantity: pq.quantity,
                // addressId: this.addresses[0].id,
              },
              supplierId: this.shipmentQuoteApp.manufacturer?.id,
              validUntil: (validUntil && formatISO(validUntil)) || null,
              isEstimated: false,
            };
          }),
          miscItems: this.shipmentQuoteApp.miscItems.map(misc => {
            return {
              name: misc.name,
              cost: misc.cost,
              discount: misc.discount,
              currency: misc.computedCurrency,
            };
          }),
          incoterm: this.shipmentQuoteApp.incoterm,
          manufacturingTime: this.shipmentQuoteApp.manufacturingTime,
          bufferTime: this.shipmentQuoteApp.bufferTime,
          takeRate: this.shipmentQuoteApp.takeRate,
          downPayment: this.shipmentQuoteApp.downPayment,
          notes: this.shipmentQuoteApp.notes,
          isOrder: this.shipmentQuoteApp.isOrder,
        };
      },
    });

    this.shipmentQuoteMutation = new CreateShipmentQuoteMutation(
      this.shipmentQuoteInput,
      null,
      { onSysErr: this.onSubmitError, onUserErr: this.onSubmitError },
      true,
    );
    this.shipmentQuoteMutation.process(
      quote => ShipmentQuoteModel.Load(quote),
      this.CompleteCreateQuote,
    );
  };

  @action onSubmitError = err => {
    console.error(err);
    this.shipmentQuoteApp.SetSubmitting(false);
    let message = err?.message;
    if (err?.length > 0) {
      // eslint-disable-next-line prefer-destructuring
      message = err[0].message;
    }
    Alert.error(message);
  };

  @action CompleteCreateQuote = quote => {
    this.shipmentQuoteApp.SetSubmitting(false);
    routerStore.navigate('quote', { quoteId: graphqlIdToDecimal(quote.id) });
  };

  validateInput = () => {
    if (this.unquotableRFQs.length > 0) {
      Alert.error(`${this.unquotableRFQs.length} RFQs are not quotable.`);
      return false;
    }
    if (this.numUnquotedRfqs > 0) {
      Alert.error(`${this.numUnquotedRfqs} rfq(s) are not quoted.`);
      return false;
    }
    if (this.addresses.length === 0) {
      Alert.error(`There are no addresses in any of the RFQs.`);
      return false;
    }
    if (this.validUntilMismatch) {
      Alert.error(
        'Some of the product quotes have a "Valid Until" date in the past.',
      );
      return false;
    }
    if (!this.areThereInstantQuotes) {
      if (!this.shipmentQuoteInput.input.shippingQuote.cost) {
        Alert.error('Missing shipping costs.');
        return false;
      }
      if (!this.shipmentQuoteInput.input.shippingQuote.deliveryTime) {
        Alert.error('Missing shipping delivery time.');
        return false;
      }
      if (!this.shipmentQuoteInput.input.shippingQuote.provider) {
        Alert.error('Missing shipping provider.');
        return false;
      }
    }
    if (this.areThereInstantQuotesButNotAll) {
      Alert.error(
        'The shipmentquote must contain either all instant quotes or no instant quotes at all.',
      );
      return false;
    }
    if (this.currencyMismatch) {
      Alert.error(
        'Currency is missing or does not match some of the product quotes.',
      );
      return false;
    }
    if (this.supplierMismatch) {
      Alert.error(
        'Manufacturer is missing or does not match some of the product quotes.',
      );
      return false;
    }
    return true;
  };

  @action Submit = () => {
    const isValid = this.validateInput();
    if (!isValid) return;

    this.shipmentQuoteApp.SetSubmitting(true);
  };

  @computed get currentRFQIds() {
    const rfqIds = this.rfqs.map(rfq => rfq.id);
    const rfqIdsFromPQs = this.productQuotes.map(pq => pq.from.id);
    return [...rfqIds, ...rfqIdsFromPQs];
  }

  buildParamsFromRFQIds = selectedRFQIds => {
    const rfqids = [];
    const pqids = [];
    selectedRFQIds.forEach(rfqId => {
      const matchingPQ = this.productQuotes.find(pq => pq.from.id === rfqId);
      if (matchingPQ) pqids.push(graphqlIdToDBId(matchingPQ.id));
      else rfqids.push(graphqlIdToDBId(rfqId));
    });
    const { customerSlug } = routerStore.route.params;
    return { rfqids, pqids, customerSlug };
  };

  navigateShipmentQuoteToIds = selectedRFQIds => {
    const newParams = this.buildParamsFromRFQIds(selectedRFQIds);
    ShipmentQuoteNavigate(routerStore, newParams);
  };

  @action OpenEditPartsModal = () => {
    modalStore.SetModalComponent(EditPartsModal, {
      customerSpaceId: this.customerSpaceId,
      selectedRFQIds: this.currentRFQIds,
      navigateShipmentQuoteToIds: this.navigateShipmentQuoteToIds,
    });
  };

  @computed get areThereInstantQuotes() {
    return this.productQuotes.some(pq => pq.isInstantQuote);
  }

  @computed get areThereInstantQuotesButNotAll() {
    const areThereNonInstantQuotes =
      this.rfqs.length > 0 || this.productQuotes.some(pq => !pq.isInstantQuote);
    return this.areThereInstantQuotes && areThereNonInstantQuotes;
  }
}
